import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useCallback, useEffect, useState} from 'react';
import {AurSwitch} from '../../common/aur-switch/AurSwitch';
import {isEmpty} from 'lodash';

export interface EstimatedBudgetValue {
  rightToViewEstimatedBudget?: boolean;
  showBudgetAlerts?: boolean;
}
export interface EstimatedBudgetProps {
  initEstimatedBudgetValue?: EstimatedBudgetValue;
  rightToViewEstimatedBudgetDisabled?: boolean;
  onChangeValue?: (value: EstimatedBudgetValue) => void;
}

interface EstimateSwitchLabelProps {
  value: boolean;
  label: string;
  disabled: boolean;
  visible: boolean;
  dataTestId: string;
  onChangeStateValue: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
}

const EstimatedBudgetView: React.FC<EstimatedBudgetProps> = (
  props: EstimatedBudgetProps
) => {
  const {
    initEstimatedBudgetValue = {},
    rightToViewEstimatedBudgetDisabled = false,
    onChangeValue,
  } = props;

  const [estimatedBudgetValue, setEstimatedBudgetValue] =
    useState<EstimatedBudgetValue>(initEstimatedBudgetValue);

  useEffect(() => {
    if (!isEmpty(initEstimatedBudgetValue)) {
      setEstimatedBudgetValue(initEstimatedBudgetValue);
    }
  }, [initEstimatedBudgetValue]);

  const updateEstimatedBudgetValue = useCallback(
    (value: Partial<EstimatedBudgetValue>) =>
      setEstimatedBudgetValue((prevValue: EstimatedBudgetValue) => ({
        ...prevValue,
        ...value,
      })),
    []
  );

  const onChangeRightToViewEstimatedBudgetValue = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      updateEstimatedBudgetValue({rightToViewEstimatedBudget: checked});
      onChangeValue?.({
        rightToViewEstimatedBudget: checked,
        showBudgetAlerts:
          checked === false
            ? false
            : (estimatedBudgetValue?.showBudgetAlerts ?? false),
      });
    },
    [
      estimatedBudgetValue?.showBudgetAlerts,
      onChangeValue,
      updateEstimatedBudgetValue,
    ]
  );

  const onChangeShowBudgetAlertsValue = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      updateEstimatedBudgetValue({showBudgetAlerts: checked});
      onChangeValue?.({
        rightToViewEstimatedBudget:
          estimatedBudgetValue?.rightToViewEstimatedBudget,
        showBudgetAlerts: checked,
      });
    },
    [
      estimatedBudgetValue?.rightToViewEstimatedBudget,
      onChangeValue,
      updateEstimatedBudgetValue,
    ]
  );

  const renderSwitchLabel = useCallback(
    (switchProps: EstimateSwitchLabelProps) => {
      const {value, label, disabled, visible, dataTestId, onChangeStateValue} =
        switchProps;
      return (
        <>
          {visible ? (
            <Box className={'switch-label-container'}>
              <AurSwitch
                value={value}
                disabled={disabled}
                onChange={onChangeStateValue}
                dataTestId={dataTestId}
                ariaLabel={`${t('aria.toggle')} ${t(
                  'eca.permisssionPanel.estimatedBudget.title'
                )}`}
              />
              <Typography
                className={`typo-text subhead4_size ${disabled ? 'grey-700' : 'mist_1300'}`}
              >
                {label}
              </Typography>
            </Box>
          ) : null}
        </>
      );
    },
    []
  );

  return (
    <Box className={'estimated-budget-container'}>
      <Box className={'estimated-budget-desc'}>
        <Typography className="typo-text subhead2_size title-text">
          {t('eca.permisssionPanel.estimatedBudget.title')}
        </Typography>
        <Typography className="typo-text body2_size subtitle-text">
          {rightToViewEstimatedBudgetDisabled
            ? t('eca.permisssionPanel.estimatedBudget.disabledSubtitle')
            : t('eca.permisssionPanel.estimatedBudget.subtitle')}
        </Typography>
      </Box>
      <Box>
        {renderSwitchLabel({
          value: estimatedBudgetValue?.rightToViewEstimatedBudget ?? false,
          label: t('eca.permisssionPanel.estimatedBudget.rightsToView'),
          visible: true,
          disabled: rightToViewEstimatedBudgetDisabled,
          dataTestId: 'toggle-righttoview-switch',
          onChangeStateValue: onChangeRightToViewEstimatedBudgetValue,
        })}
        {(estimatedBudgetValue?.rightToViewEstimatedBudget ?? false) &&
          !rightToViewEstimatedBudgetDisabled && (
            <Box className="estimated-budget-switch-div">
              {renderSwitchLabel({
                value: estimatedBudgetValue?.showBudgetAlerts ?? false,
                label: t(
                  'eca.permisssionPanel.estimatedBudget.showBudgetAlerts'
                ),
                visible:
                  (estimatedBudgetValue?.rightToViewEstimatedBudget ?? false) &&
                  !rightToViewEstimatedBudgetDisabled,
                disabled: false,
                dataTestId: 'toggle-showbudgetalerts-switch',
                onChangeStateValue: onChangeShowBudgetAlertsValue,
              })}
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default EstimatedBudgetView;
